import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState([]);
  const [testRides, setTestRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalBookings: 0,
    confirmedBookings: 0,
    pendingBookings: 0,
    cancelledBookings: 0,
    totalTestRides: 0,
    pendingTestRides: 0,
    confirmedTestRides: 0
  });

  // API URL
  const API_URL = 'https://janvaahan.com/backend/public/api';

  useEffect(() => {
    // Set active tab from URL query param
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['overview', 'bookings', 'test-rides', 'profile'].includes(tab)) {
      setActiveTab(tab);
    }
    
    fetchUserData();
  }, [location]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Please login to view your dashboard');
        return;
      }
      
      // Fetch bookings
      const bookingsResponse = await axios.get(`${API_URL}/my-bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch test rides
      const testRidesResponse = await axios.get(`${API_URL}/my-test-rides`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Handle different response structures
      const bookingsData = bookingsResponse.data?.data || bookingsResponse.data || [];
      const testRidesData = testRidesResponse.data?.data || testRidesResponse.data || [];
      
      setBookings(bookingsData);
      setTestRides(testRidesData);
      
      // Calculate stats
      setStats({
        totalBookings: bookingsData.length,
        confirmedBookings: bookingsData.filter(b => b.status === 'confirmed').length,
        pendingBookings: bookingsData.filter(b => b.status === 'pending').length,
        cancelledBookings: bookingsData.filter(b => b.status === 'cancelled').length,
        totalTestRides: testRidesData.length,
        pendingTestRides: testRidesData.filter(t => t.status === 'pending').length,
        confirmedTestRides: testRidesData.filter(t => t.status === 'confirmed').length
      });
      
    } catch (error) {
      console.error('Error fetching user data:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError(error.response?.data?.message || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const getStatusBadge = (status) => {
    switch(status?.toLowerCase()) {
      case 'confirmed':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">Confirmed</span>;
      case 'cancelled':
        return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">Cancelled</span>;
      case 'pending':
        return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">Pending</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold">{status || 'N/A'}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
          <Link to="/login" className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 inline-block">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                Welcome back, {user?.name?.split(' ')[0] || 'User'}!
              </h1>
              <p className="text-red-100">
                Manage your bookings, test rides, and account settings
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-3">
              <Link
                to="/book"
                className="bg-white text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition transform hover:scale-105"
              >
                Book New Bike
              </Link>
              <Link
                to="/test-ride"
                className="border-2 border-white text-white px-6 py-2 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition transform hover:scale-105"
              >
                Test Ride
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Bookings</p>
                <p className="text-3xl font-bold text-gray-800">{stats.totalBookings}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Confirmed Bookings</p>
                <p className="text-3xl font-bold text-green-600">{stats.confirmedBookings}</p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Pending Bookings</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pendingBookings}</p>
              </div>
              <div className="bg-yellow-100 rounded-full p-3">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Test Rides</p>
                <p className="text-3xl font-bold text-purple-600">{stats.totalTestRides}</p>
              </div>
              <div className="bg-purple-100 rounded-full p-3">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap gap-4 sm:space-x-8">
            {['overview', 'bookings', 'test-rides', 'profile'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 px-1 text-sm font-medium capitalize transition ${
                  activeTab === tab
                    ? 'border-b-2 border-red-600 text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'test-rides' ? 'Test Rides' : tab === 'bookings' ? 'My Bookings' : tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Welcome Card */}
              <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-2">Your Journey with Janvahan</h2>
                <p className="text-gray-300 mb-6">
                  Thank you for choosing electric mobility. We're excited to have you on board!
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-400">0</div>
                    <div className="text-sm">CO2 Emissions Saved (kg)</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-400">0</div>
                    <div className="text-sm">Km Traveled</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-400">0</div>
                    <div className="text-sm">Trees Planted</div>
                  </div>
                </div>
              </div>

              {/* Recent Bookings */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4">Recent Bookings</h3>
                {bookings.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No bookings yet.</p>
                    <Link to="/products" className="text-red-600 hover:underline mt-2 inline-block">
                      Browse Bikes →
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bookings.slice(0, 3).map((booking) => (
                      <div key={booking.id} className="border rounded-lg p-4 hover:shadow-md transition">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                          <div className="flex-1">
                            <p className="font-semibold">{booking.product?.name || 'N/A'}</p>
                            <p className="text-sm text-gray-500">Booking ID: {booking.booking_number}</p>
                            <p className="text-sm text-gray-500">Amount: ₹{booking.amount}</p>
                            <p className="text-sm text-gray-500">Date: {formatDate(booking.created_at)}</p>
                          </div>
                          <div>
                            {getStatusBadge(booking.status)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {bookings.length > 3 && (
                      <button
                        onClick={() => setActiveTab('bookings')}
                        className="text-red-600 hover:underline text-sm"
                      >
                        View all {bookings.length} bookings →
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Recent Test Rides */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4">Recent Test Rides</h3>
                {testRides.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No test rides scheduled.</p>
                    <Link to="/test-ride" className="text-red-600 hover:underline mt-2 inline-block">
                      Schedule a Test Ride →
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {testRides.slice(0, 3).map((ride) => (
                      <div key={ride.id} className="border rounded-lg p-4">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                          <div className="flex-1">
                            <p className="font-semibold">{ride.product?.name || 'N/A'}</p>
                            <p className="text-sm text-gray-500">Date: {formatDate(ride.preferred_date)}</p>
                            <p className="text-sm text-gray-500">Time: {formatTime(ride.preferred_time)}</p>
                          </div>
                          <div>
                            {getStatusBadge(ride.status)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {testRides.length > 3 && (
                      <button
                        onClick={() => setActiveTab('test-rides')}
                        className="text-red-600 hover:underline text-sm"
                      >
                        View all {testRides.length} test rides →
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">My Bookings</h2>
                <div className="text-sm text-gray-500">
                  Total: {bookings.length} bookings
                </div>
              </div>
              {bookings.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📦</div>
                  <p className="text-gray-500 mb-4">You haven't made any bookings yet.</p>
                  <Link to="/products" className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 inline-block">
                    Browse Electric Bikes
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="border rounded-lg p-6 hover:shadow-lg transition">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold">{booking.product?.name || 'N/A'}</h3>
                            {getStatusBadge(booking.status)}
                          </div>
                          <p className="text-gray-600 text-sm mb-2">Model: {booking.product?.model || 'N/A'}</p>
                          <p className="text-gray-600 text-sm">Booking ID: {booking.booking_number}</p>
                          <p className="text-gray-600 text-sm">Booking Date: {formatDate(booking.created_at)}</p>
                          <p className="text-gray-600 text-sm">Amount: ₹{booking.amount}</p>
                          {booking.customer_details && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-sm font-semibold">Delivery Address:</p>
                              <p className="text-sm text-gray-600">{booking.customer_details.address}</p>
                              <p className="text-sm text-gray-600">{booking.customer_details.city} - {booking.customer_details.pincode}</p>
                            </div>
                          )}
                        </div>
                        <div>
                          <Link
                            to={`/product/${booking.product_id}`}
                            className="text-red-600 hover:underline text-sm whitespace-nowrap"
                          >
                            View Details →
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Test Rides Tab */}
          {activeTab === 'test-rides' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Test Ride Requests</h2>
                <div className="text-sm text-gray-500">
                  Total: {testRides.length} test rides
                </div>
              </div>
              {testRides.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🚗</div>
                  <p className="text-gray-500 mb-4">You haven't scheduled any test rides yet.</p>
                  <Link to="/test-ride" className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 inline-block">
                    Schedule a Test Ride
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {testRides.map((ride) => (
                    <div key={ride.id} className="border rounded-lg p-6">
                      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold">{ride.product?.name || 'N/A'}</h3>
                            {getStatusBadge(ride.status)}
                          </div>
                          <p className="text-gray-600">Preferred Date: {formatDate(ride.preferred_date)}</p>
                          <p className="text-gray-600">Preferred Time: {formatTime(ride.preferred_time)}</p>
                          <p className="text-gray-600">Contact: {ride.phone}</p>
                          {ride.location && (
                            <p className="text-gray-600">Location: {ride.location}</p>
                          )}
                          {ride.message && (
                            <p className="text-gray-600 mt-2 text-sm">Message: {ride.message}</p>
                          )}
                          {ride.status === 'pending' && (
                            <p className="text-sm text-yellow-600 mt-2">
                              ⏳ Your test ride request is being processed. We'll contact you soon!
                            </p>
                          )}
                          {ride.status === 'confirmed' && (
                            <p className="text-sm text-green-600 mt-2">
                              ✓ Test ride confirmed! Our representative will contact you.
                            </p>
                          )}
                          {ride.status === 'cancelled' && (
                            <p className="text-sm text-red-600 mt-2">
                              ✗ This test ride has been cancelled.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-6">Profile Information</h2>
              <div className="max-w-2xl">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{user?.name}</h3>
                    <p className="text-gray-600">Member since {formatDate(user?.created_at)}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="border-b pb-4">
                    <label className="text-sm text-gray-500">Full Name</label>
                    <p className="text-lg font-medium">{user?.name || 'N/A'}</p>
                  </div>
                  <div className="border-b pb-4">
                    <label className="text-sm text-gray-500">Email Address</label>
                    <p className="text-lg font-medium">{user?.email || 'N/A'}</p>
                  </div>
                  <div className="border-b pb-4">
                    <label className="text-sm text-gray-500">Account Type</label>
                    <p className="text-lg font-medium capitalize">{user?.role || 'User'}</p>
                  </div>
                  <div className="border-b pb-4">
                    <label className="text-sm text-gray-500">Member Since</label>
                    <p className="text-lg font-medium">{formatDate(user?.created_at)}</p>
                  </div>
                </div>
                
                <div className="mt-8 flex flex-wrap gap-4">
                  <button
                    onClick={logout}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
                  >
                    Logout
                  </button>
                  <Link
                    to="/"
                    className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition"
                  >
                    Back to Home
                  </Link>
                  <Link
                    to="/products"
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition"
                  >
                    Browse Bikes
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;